// External imports
import React, { Fragment } from 'react';
import {
  Button,
  Divider,
  Snackbar,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Paper,
  Checkbox,
  withStyles,
} from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
// Internal imports
import { LOGO_TYPE } from '~/app/constants.js';
import LogoSelector from '~/app/Pages/Setup/AccountSetup/components/LogoSelector/LogoSelector.jsx';
import TechSupportNumber from '~/app/Pages/Setup/AccountSetup/components/TechSupportNumber.jsx';
import SimpleDialog from '#/Common/SimpleDialog.jsx';
import XlinkAPI from '~/app/api/xlinkAPI';
import WebHelpers from '~/app/webHelpers.js';
import ErrorHelpers from '~/app/errorHelpers.js';
import SetupPage from '~/app/Components/Settings/Setup/SetupPage.jsx';
// Redux imports
import { connect } from 'react-redux';
import { actions as cobrandActions } from '~/app/redux/cobrand/duck';
// Styling imports
import { styles } from '~/app/Pages/Setup/AccountSetup/CobrandingSetup/css/cobrandingSetup.js';
import '~/app/Pages/Setup/AccountSetup/CobrandingSetup/css/cobrandingSetup.css';

const mapStateToProps = state => {
  return {
    isLoading: state.cobrand.isLoadingCobrandStatus,
    logo: state.cobrand.logo,
    canCobrand: state.cobrand.canCobrand,
    hasPurchasedCobranding: state.cobrand.hasPurchasedCobranding,
    techSupportNum: state.cobrand.techSupportNumber,
    hasAccessToAddCobranding: state.cobrand.hasAccessToAddCobranding,
    hasAccessToPurchaseCobranding: state.cobrand.hasAccessToPurchaseCobranding,
  };
};

const mapDispatchToProps = {
  ...cobrandActions,
};

class CobrandingSetup extends SetupPage {
  constructor(props) {
    const name = 'Cobranding'; // You need this for save and next to transition
    super(props, name);
    this.state = {
      confirmCancel: false,
      showSuccess: false,
      hasCobranding: this.props.canCobrand,
      hasPurchasedCobranding: this.props.hasPurchasedCobranding,
    };
    this.payload = WebHelpers.getJWTPayload();
  }

  getInitialConfigState() {
    this.setState({
      confirmCancel: false,
    });
  }

  toggleConfirmCancel = toggle => {
    if (!(this.props.setupTourLinkClicked || this.props.setupTourRestart)) {
      this.setState({
        confirmCancel: toggle,
      });
    }
  };

  // Handle onChange without validation for checkbox
  handleCheckbox = event => {
    if (event.target.name === 'hasPurchasedCobranding' && !event.target.checked) {
      this.setState({ [event.target.name]: event.target.checked, hasCobranding: false });
    } else this.setState({ [event.target.name]: event.target.checked });
  };

  submitCobrandSetupData = async () => {
    try {
      await XlinkAPI.UpdateCobrandingStatuses(
        this.state.hasCobranding ? 1 : 0,
        this.state.hasPurchasedCobranding ? 1 : 0,
      );

      // Retrieve logo settings
      this.props.fetchLogo(true);
    } catch (error) {
      ErrorHelpers.handleError('Failed to update Office setup', error);
    }

    this.props.fetchCobrandingPermission();
  };

  render() {
    const { classes } = this.props;
    return (
      <Paper>
        <form className="cobranding-setup-form">
          <div className="cobranding-setup-content">
            <span className="cobranding-setup-section-header">Cobranding</span>
            <div className="cobranding-setup-section-content">
              For assistance with cobranding please contact your software provider. Allowing your
              software provider to assist with logo creation may lend better results.
            </div>
            <span className="cobranding-setup-section-header">
              CrossLink Design & Brand Guidelines
            </span>
            <div className="cobranding-setup-section-content">
              <a
                href="https://www.crosslinktax.com/wp-content/uploads/2026/07/CLO-CoBranding-Guide-2026.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                <b>Click here to review</b>
              </a>
            </div>
          </div>
          {!this.props.isLoading && this.props.hasAccessToPurchaseCobranding && (
            <FormControl fullWidth>
              <div className="cobranding-setup-checkbox-block">
                <FormControlLabel
                  label="Purchased Cobranding"
                  classes={{ root: classes.FormControlLabel }}
                  control={
                    <Checkbox
                      icon={
                        <CheckBoxOutlineBlankIcon
                          classes={{ root: classes.checkboxOutline }}
                          color="secondary"
                        />
                      }
                      checkedIcon={<CheckBoxIcon color="primary" />}
                      id="chkhasPurchasedCobranding"
                      name="hasPurchasedCobranding"
                      checked={this.state.hasPurchasedCobranding}
                      onChange={this.handleCheckbox}
                    />
                  }
                />
              </div>
              <FormHelperText classes={{ root: classes.FormHelperText }}>
                You are seeing this field because you have proper access level.
              </FormHelperText>
            </FormControl>
          )}
          {!this.props.isLoading &&
            (this.props.hasAccessToAddCobranding ||
              (this.props.hasAccessToPurchaseCobranding && this.state.hasPurchasedCobranding)) && (
              <FormControl fullWidth>
                <div className="office-setup-checkbox-block">
                  <FormControlLabel
                    label="Enable Cobranding"
                    classes={{ root: classes.FormControlLabel }}
                    control={
                      <Checkbox
                        icon={
                          <CheckBoxOutlineBlankIcon
                            classes={{ root: classes.checkboxOutline }}
                            color="secondary"
                          />
                        }
                        checkedIcon={<CheckBoxIcon color="primary" />}
                        id="chkToggleCobranding"
                        name="hasCobranding"
                        checked={this.state.hasCobranding}
                        onChange={this.handleCheckbox}
                      />
                    }
                  />
                </div>
                <FormHelperText classes={{ root: classes.FormHelperText }}>
                  You are seeing this field because you have proper access level.
                </FormHelperText>
              </FormControl>
            )}
          {this.props.canCobrand && !this.props.isLoading && (
            <Fragment>
              <LogoSelector
                logoType={LOGO_TYPE.ACCOUNT}
                loginID={this.props.loginID}
                hierarchyType={this.props.hierarchyType}
                setLogo={this.props.setLogo}
                logo={this.props.logo}
                hasCobranding={this.props.canCobrand}
                requestUploadLogo={this.props.requestUploadLogo}
              />
              <TechSupportNumber
                techSupportNum={this.props.techSupportNum}
                showSuccess={() => this.setState({ showSuccess: true })}
              />
            </Fragment>
          )}
          <div>
            <Divider
              classes={{
                root: classes.documentDivider,
              }}
            />
            <br />
            <div className="setup-nav-btns">
              {this.hasPrev() ? (
                <Button
                  id="btnPrevCobrandingSetup"
                  color="primary"
                  onClick={() => this.toggleConfirmCancel(true)}
                >
                  Previous (cancel)
                </Button>
              ) : null}
              {this.hasNext() ? (
                <Button
                  id="btnNextCobrandingSetup"
                  color="primary"
                  onClick={() => {
                    this.submitCobrandSetupData();
                    this.getNextPage();
                  }}
                >
                  Save &amp; Next
                </Button>
              ) : null}
            </div>

            <br />

            <Snackbar
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              open={this.state.showSuccess}
              autoHideDuration={2500}
              onClose={() => this.setState({ showSuccess: false })}
              id="snkbarconfsaved"
              message={<span id="success-message">Configuration Saved</span>}
            />

            <SimpleDialog
              open={this.state.confirmCancel}
              onClose={() => this.toggleConfirmCancel(false)}
              onConfirm={() => {
                this.toggleConfirmCancel(false);
                this.getInitialConfigState();
                this.getPrevPage();
              }}
              dialogTitle={'Lose Unsaved Changes?'}
              contentText={'Are you sure you want to undo any pending changes?'}
            />
          </div>
        </form>
      </Paper>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(CobrandingSetup));
